import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronDownIcon, BellIcon, SunIcon, MoonIcon } from "@heroicons/react/24/solid";
import { getUserDetails } from "../../utils/getUserDetails";
import { useAlert } from "../../constants/AlertProvider";
import { useTheme } from "../../constants/ThemeProvider";
import Logo from "../../../assets/images/un_resume_logo2.png";
import ProfilePic from "../../../assets/images/blank-profile-pic.png";
import Notifications from "../candidate/Notifications";

export default function HomeNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { theme, toggleTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [userData, setUserData] = useState({ name: "" });
  const [logo, setLogo] = useState("");
  const showAlert = useAlert();

  const toggleNotifications = () => setShowNotifications(!showNotifications);


  const handleLogout = async () => {
    try {
      const csrfToken = Cookies.get("csrftoken");
      await axios.post("/api/logout/", null, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
      });
      navigate("/login");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userDetails = await getUserDetails();
        if (!userDetails?.id || !userDetails.company) {
          showAlert("User or Company details not found.", "error");
          return;
        }

        const [userRes, companyRes] = await Promise.all([
          axios.get(`/api/users/${userDetails.id}/`, { withCredentials: true }),
          axios.get(`/api/company/${userDetails.company}/`, { withCredentials: true })
        ]);

        setUserData({ name: userRes.data.name });
        setLogo(companyRes.data.logo);
      } catch (error) {
        showAlert("Error Fetching Profile Data.", "error");
        console.error("error", error);
      }
    }

    fetchData();
  }, [showAlert]);

  return (
    <nav
      className="border-b border-b-gray-300 dark:border-b-gray-700 fixed top-0 left-0 right-0 z-50 flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 bg-white dark:bg-gray-900 w-full"
    >
      {/* Logo */}
      <div className="flex items-center gap-2">
        <img alt="UnResume Logo" className="w-9 h-9 rounded-full shadow-sm" src={Logo} />
        <Link className="text-xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-200 tracking-tighter">
          UnResumé
        </Link>
        <div className="hidden ml-6 md:flex flex-row gap-x-6">
          <div className={`h-16 flex items-center ${currentPath === "/orghome" ? "border-b-3 border-blue-700" : ""}`}>
            <Link
              to="/orghome"
              className={`text-sm font-medium transition-colors duration-200 ${currentPath === "/orghome" ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                }`}
            >
              Talent
            </Link>
          </div>

          <div className={`h-16 flex items-center ${currentPath === "/matches" ? "border-b-3 border-blue-700" : ""}`}>
            <Link
              to="/matches"
              className={`text-sm font-medium transition-colors duration-200 ${currentPath === "/matches" ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                }`}
            >
              Matches
            </Link>
          </div>
          <div className={`h-16 flex items-center ${currentPath === "/myjobs" ? "border-b-3 border-blue-700" : ""}`}>
            <Link
              to="/myjobs"
              className={`text-sm font-medium transition-colors duration-200 ${currentPath === "/myjobs" ? "text-blue-600 dark:text-blue-400 font-semibold" : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                }`}
            >
              Opportunities
            </Link>
          </div>
          <div className={`h-16 flex items-center ${currentPath === "/" ? "border-b-3 border-blue-700" : ""}`}>
            <Link
              to="#"
              className={`text-sm font-medium transition-colors duration-200 ${currentPath === "/" ? "text-blue-600 dark:text-blue-400 font-semibold" : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                }`}
            >
              Community
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center gap-2">
        {/* Theme toggle */}
        <button
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
          type="button"
          onClick={toggleTheme}
        >
          {theme === "dark" ? (
            <SunIcon className="w-5 h-5 text-gray-300" />
          ) : (
            <MoonIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Notifications */}
        <button
          onClick={toggleNotifications}
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
        >
          <BellIcon className="w-6 h-6 text-gray-400" />
        </button>

        {/* Profile Dropdown */}
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none">
            <img alt="Profile Pic" className="w-8 h-8 rounded-full border-2 border-blue-200" src={logo || ProfilePic} />
            <ChevronDownIcon className="w-4 h-4 text-blue-600" />
          </MenuButton>
          <Transition
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 shadow-lg focus:outline-none rounded-lg py-2 ring-1 ring-blue-200/20 dark:ring-gray-700">
              <p className="block px-4 py-2 text-base font-semibold text-gray-700 dark:text-gray-300">
                Hello,{" "}
                <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                  {userData.name}
                </span>
              </p>
              <MenuItem>
                {({ active }) => (
                  <Link
                    to="/orgprofile"
                    state={{ from: 'edit' }}
                    className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                  >
                    Profile
                  </Link>
                )}
              </MenuItem>
              <MenuItem>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={`block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 ${active ? "bg-red-50 dark:bg-red-900/30" : ""}`}
                  >
                    Logout
                  </button>
                )}
              </MenuItem>
            </MenuItems>
          </Transition>
        </Menu>
      </div>

      {/* Mobile Menu */}
      <Menu as="div" className="relative md:hidden flex flex-row gap-x-2">
        <button
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
          type="button"
          onClick={toggleTheme}
        >
          {theme === "dark" ? (
            <SunIcon className="w-5 h-5 text-gray-300" />
          ) : (
            <MoonIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>
        <button
          onClick={toggleNotifications}
          className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none"
        >
          <BellIcon className="w-6 h-6 text-gray-400" />
        </button>
        <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-blue-100 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none">
          <img alt="Profile Pic" className="w-8 h-8 rounded-full border-2 border-blue-200" src={logo || ProfilePic} />
          <ChevronDownIcon className="w-4 h-4 text-blue-600" />
        </MenuButton>
        <Transition
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <MenuItems className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 shadow-lg focus:outline-none rounded-lg py-2 ring-1 ring-blue-200/20 dark:ring-gray-700">
            <p className={"block px-4 py-2 text-base font-semibold text-gray-700 dark:text-gray-300"}>Hello, {userData.name}</p>
            <MenuItem>
              {({ active }) => (
                <Link
                  to="/orghome"
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                >
                  Talent
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  to="/matches"
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}${currentPath === "/matches" ? " font-semibold text-blue-600 dark:text-blue-400" : ""}`}
                >
                  Matches
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  to="/myjobs"
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                >
                  Opportunities
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  to="#"
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                >
                  Community
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <Link
                  to="/orgprofile"
                  className={`block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 ${active ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : ""}`}
                >
                  Profile
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 ${active ? "bg-red-50 dark:bg-red-900/30" : ""}`}
                >
                  Logout
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Transition>
      </Menu>

      {/* Notifications */}
      <Transition
        show={showNotifications}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 translate-x-10"
        enterTo="opacity-100 translate-x-0"
        leave="transition ease-in duration-200"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo="opacity-0 translate-x-10"
      >
        <div className="fixed z-[9999] top-16 right-4 w-80 max-w-xs mt-2 h-[90vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-auto border border-blue-200/50 dark:border-gray-700">
          <Notifications onClose={toggleNotifications} />
        </div>
      </Transition>
    </nav>
  );
}
